import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PostCard from "@/components/PostCard";

const CATEGORIES = ["Philosophy", "Spirituality", "Science", "Personal Reflection"];

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/posts");
        setPosts(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 pt-20 md:pt-32 pb-20 md:pb-28 border-b border-black/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 fade-rise">
            <div className="text-[11px] uppercase tracking-[0.32em] text-black/55 mb-8">
              An open journal · since {new Date().getFullYear()}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[0.95] tracking-tighter">
              Notes from
              <br />
              the long looking
              <span className="inline-block w-2 h-2 ml-3 bg-black align-middle" />
            </h1>
          </div>
          <div className="md:col-span-4 fade-rise fade-rise-delay-2">
            <p className="text-base md:text-lg text-black/65 leading-relaxed max-w-md">
              A slow, honest blog about the search for what is true — across
              philosophy, spirit, science, and the texture of a single life.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-black/50">
              {CATEGORIES.map((c) => (
                <Link
                  key={c}
                  to={`/category/${encodeURIComponent(c)}`}
                  className="link-underline hover:text-black"
                  data-testid={`hero-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {c}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feed */}
      <section className="px-6 md:px-12 lg:px-24 pt-20 md:pt-28">
        <div className="flex items-baseline justify-between mb-12">
          <h2 className="font-serif text-3xl md:text-4xl font-normal">
            Latest entries
          </h2>
          <div className="text-[10px] uppercase tracking-[0.28em] text-black/45">
            {posts.length} {posts.length === 1 ? "entry" : "entries"}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-sm text-black/40 tracking-widest uppercase">
            Loading…
          </div>
        ) : posts.length === 0 ? (
          <div className="py-32 text-center">
            <p className="font-serif text-2xl text-black/60 italic">
              Nothing here yet. The looking is still beginning.
            </p>
          </div>
        ) : (
          <div data-testid="post-feed">
            {featured && <PostCard post={featured} featured />}
            <div>
              {rest.map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
